import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import {
	GanttLineColors,
	WELD165_COLORS,
	WELD165_NAME,
	WELD_LINE_PALETTE,
} from "@/shared/colors";
import {
	GroupedDelayAsset,
	getDelayAssetsGrouped,
} from "@/shared/repositories/DelayAssetRepository";
import { useUserScope } from "@/setup/scope";

export const WELD_AREA_NAME = "Welds";

export interface WeldLine {
	AssetId: number;
	AssetName: string;
	colors: GanttLineColors;
}

interface UseWeldLinesProps {
	enabled?: boolean;
	onError?: (error: Error) => void;
}

export const useWeldLines = ({
	enabled = true,
	onError,
}: UseWeldLinesProps = {}) => {
	const { data, isLoading } = useQuery<GroupedDelayAsset[], Error>({
		queryKey: ["weldLineAssets"],
		queryFn: getDelayAssetsGrouped,
		enabled,
		staleTime: 5 * 60 * 1000,
		onError,
	});

	const scope = useUserScope();

	const weldLines = useMemo<WeldLine[]>(() => {
		if (!data) return [];
		let paletteIdx = 0;
		return data
			.filter(
				(a) =>
					a.ParentAssetName === WELD_AREA_NAME && scope.includes(a.AssetId),
			)
			.sort((a, b) => a.AssetName.localeCompare(b.AssetName))
			.map((a) => ({
				AssetId: a.AssetId,
				AssetName: a.AssetName,
				colors:
					a.AssetName === WELD165_NAME
						? WELD165_COLORS
						: WELD_LINE_PALETTE[paletteIdx++ % WELD_LINE_PALETTE.length],
			}));
	}, [data, scope]);

	return { weldLines, isLoading };
};
